import { useContext, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import {
  Box,
  Checkbox,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  Typography,
} from '@mui/material';
import { Inventory2Outlined } from '@mui/icons-material';
import { OrdensContext } from 'contexts/OrdensContext';
import { DropsContext } from 'contexts/DropsContext';
import { useModal } from 'components/Modals';
import InputMask from 'components/InputMask';
import Button from 'components/Button';
import Dropdown from 'components/Dropdown';

const PauseModal = ({ processo, onSubmit }) => {
  const defaultValues = {
    quantidade: '',
    retrabalho: '',
    perda: '',
    nova_ordem: false,
    troco: false,
    tipo_parada_id: null,
  };
  const { closeModal } = useModal();
  const { watch, setValue, control, handleSubmit } = useForm({ defaultValues });
  const { postLoading } = useContext(OrdensContext);
  const { drops } = useContext(DropsContext);
  const loteReposicao = Number(processo?.lote_reposicao || 0);
  const possuiLoteReposicao = loteReposicao > 0;
  const quantidade = watch('quantidade');
  const perda = watch('perda');
  const podeGerarNovaOrdem =
    processo?.registra_perda === 'SIM' && Number(perda) > 0;
  const podeGerarTroco =
    processo?.registra_troco === 'SIM' &&
    quantidade !== '' &&
    Number.isFinite(Number(quantidade)) &&
    Number(quantidade) >= 0 &&
    Number(quantidade) < Number(processo?.quantidade_produzir || 0);

  useEffect(() => {
    if (!podeGerarNovaOrdem) setValue('nova_ordem', false);
  }, [podeGerarNovaOrdem, setValue]);

  useEffect(() => {
    if (!podeGerarTroco) setValue('troco', false);
  }, [podeGerarTroco, setValue]);

  return (
    <>
      <DialogTitle>Informar Produção</DialogTitle>
      <DialogContent>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Paper
              variant="outlined"
              sx={{ p: 2, borderRadius: 2, bgcolor: 'action.hover' }}
            >
              <Grid container spacing={2}>
                <Grid item xs={12} display="flex" alignItems="center" gap={2}>
                  <Inventory2Outlined color="primary" />
                  <Typography variant="subtitle1" fontWeight="bold">
                    Dados da Produção
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Produto
                  </Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {processo?.produto || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Referência
                  </Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {processo?.referencia || '-'}
                  </Typography>
                </Grid>
                {possuiLoteReposicao && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">
                      Quantidade do lote para reposição
                    </Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {processo?.lote_reposicao} {processo?.unidade_medida}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Paper>
          </Grid>
          <Grid item xs={12}>
            <InputMask
              name="quantidade"
              control={control}
              label="Quantidade Produzida"
            />
          </Grid>
          {processo?.registra_perda === 'SIM' && (
            <Grid item xs={12}>
              <InputMask
                name="retrabalho"
                control={control}
                label="Quantidade de Retrabalho"
              />
            </Grid>
          )}
          {processo?.registra_perda === 'SIM' && (
            <Grid item xs={12}>
              <InputMask
                name="perda"
                control={control}
                label="Quantidade Perdida"
              />
            </Grid>
          )}
          {podeGerarNovaOrdem && (
            <Grid item xs={12} display="flex" alignItems="center">
              <Checkbox
                color="primary"
                checked={watch('nova_ordem')}
                onChange={(_, value) => setValue('nova_ordem', value)}
                inputProps={{
                  'aria-labelledby': 'nova-ordem-label',
                  'aria-describedby': 'nova-ordem-descricao',
                }}
              />
              <Box>
                <Typography id="nova-ordem-label" variant="body2">
                  Gerar Nova Ordem de Produção
                </Typography>
                <Typography
                  id="nova-ordem-descricao"
                  variant="caption"
                  color="text.secondary"
                  display="block"
                >
                  Gera uma nova ordem de produção com a quantidade informada no
                  campo Quantidade Perdida.
                </Typography>
              </Box>
            </Grid>
          )}
          {podeGerarTroco && (
            <Grid item xs={12} display="flex" alignItems="center">
              <Checkbox
                color="primary"
                checked={watch('troco')}
                onChange={(_, value) => setValue('troco', value)}
                inputProps={{
                  'aria-labelledby': 'troco-label',
                  'aria-describedby': 'troco-descricao',
                }}
              />
              <Box>
                <Typography id="troco-label" variant="body2">
                  Gerar Troco
                </Typography>
                <Typography
                  id="troco-descricao"
                  variant="caption"
                  color="text.secondary"
                  display="block"
                >
                  Gera uma nova ordem de produção a partir do processo atual, com
                  a quantidade restante a produzir.
                </Typography>
              </Box>
            </Grid>
          )}
          <Grid item xs={12}>
            <Dropdown
              control={control}
              name="tipo_parada_id"
              label="Tipo de Parada"
              options={drops?.TipoParada}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button
          color="primary"
          variant="contained"
          loading={postLoading}
          onClick={handleSubmit(onSubmit)}
        >
          Confirmar
        </Button>
        <Button variant="contained" color="secondary" onClick={closeModal}>
          Voltar
        </Button>
      </DialogActions>
    </>
  );
};

export default PauseModal;
